import {
  DATE_FORMAT,
  DEFAULT_VENTRATA_OPTION,
  DEFAULT_VENTRATA_PRODUCT,
  DEFAULT_VENTRATA_UNIT,
  MONTH_FORMAT,
} from 'consts'
import {useGetAvailabilitiesMutation} from 'hooks/ventrata/useGetAvailabilitiesMutation'
import {useGetCalendarMutation} from 'hooks/ventrata/useGetCalendarMutation'
import {useGetProductMutation} from 'hooks/ventrata/useGetProductMutation'
import {map, size, sum, values} from 'lodash'
import moment from 'moment'
import {EnvironmentProvider} from 'providers/EnvironmentProvider'
import React, {useContext, useEffect, useState} from 'react'
import {Ventrata} from 'types'
import {GetterMode} from 'types/basic'
import {convertUnitCounterMapToRequestUnitCounters} from 'utils/convertUnitCounterMapToRequestUnitCounters'
import {AutoCompleteHelper} from './AutoCompleteHelper'
import {InputProvider} from './InputProvider'

type Props = {
  children: React.ReactNode
}

export type VentrataData = {
  product: Ventrata.Product
  calendar: Record<string, Ventrata.CalendarDay[]>
  availabilities: Record<string, Ventrata.Availability[]>
}

type VentrataDataValue = {
  data: VentrataData
  error: Error | null
  isPending: boolean
}

const DEFAULT_VENTRATA_VALUE = {
  data: {
    product: DEFAULT_VENTRATA_PRODUCT,
    calendar: {},
    availabilities: {},
  },
  error: null,
  isPending: false,
}

const VentrataDataContext = React.createContext<VentrataDataValue>(DEFAULT_VENTRATA_VALUE)

export const VentrataDataProvider = ({children}: Props) => {
  const [value, setValue] = useState<VentrataDataValue>(DEFAULT_VENTRATA_VALUE)
  const currency = EnvironmentProvider.useCurrency()
  const productId = InputProvider.useProductId()
  const optionId = InputProvider.useOptionId('optional')
  const date = InputProvider.useDate('optional')
  const month = InputProvider.useMonth()
  const unitCounters = InputProvider.useUnitCounters('optional')
  const changeLastOperation = AutoCompleteHelper.useChangeLastOperation()

  const getProductMutation = useGetProductMutation()
  const getProduct = getProductMutation.mutate

  const getAvailabilitiesMutation = useGetAvailabilitiesMutation()
  const getAvailabilities = getAvailabilitiesMutation.mutate

  const getCalendarMutation = useGetCalendarMutation()
  const getCalendar = getCalendarMutation.mutate

  const error = getProductMutation.error || getAvailabilitiesMutation.error || getCalendarMutation.error

  const isPending = getProductMutation.isPending || getAvailabilitiesMutation.isPending || getCalendarMutation.isPending

  useEffect(() => {
    getProduct(productId, {
      onSuccess: (product) => {
        changeLastOperation('productLoading')

        setValue((value) => ({...value, data: {...value.data, product}}))
      },
    })
  }, [productId, getProduct, changeLastOperation])

  useEffect(() => {
    if (!value.data.product.id || !date || sum(values(unitCounters)) === 0) {
      return
    }

    getAvailabilities(
      {
        productId,
        optionIds: optionId ? [optionId] : map(value.data.product.options, 'id'),
        localDateStart: date,
        localDateEnd: date,
        units: convertUnitCounterMapToRequestUnitCounters(unitCounters),
        currency,
      },
      {
        onSuccess: (availabilities) => {
          changeLastOperation('availabilitiesLoading')

          setValue((value) => ({...value, data: {...value.data, availabilities}}))
        },
      }
    )
  }, [value.data.product, date, unitCounters, getAvailabilities, currency, productId, optionId, changeLastOperation])

  useEffect(() => {
    if (!value.data.product.id || sum(values(unitCounters)) === 0) {
      return
    }

    const optionIds = optionId ? [optionId] : map(value.data.product.options, 'id')

    getCalendar(
      {
        productId,
        optionIds,
        localDateStart: moment(month, MONTH_FORMAT).format(DATE_FORMAT),
        localDateEnd: moment(month, MONTH_FORMAT).endOf('month').format(DATE_FORMAT),
        units: convertUnitCounterMapToRequestUnitCounters(unitCounters),
        currency,
      },
      {
        onSuccess: (calendar) => {
          changeLastOperation('calendarLoading')

          setValue((value) => ({
            ...value,
            data: {
              ...value.data,
              calendar,
            },
          }))
        },
      }
    )
  }, [month, value.data.product, productId, optionId, unitCounters, currency, getCalendar, changeLastOperation])

  useEffect(() => {
    setValue((value) => ({...value, error}))
  }, [error])

  useEffect(() => {
    setValue((value) => ({...value, isPending}))
  }, [isPending])

  return <VentrataDataContext.Provider value={value}>{children}</VentrataDataContext.Provider>
}

VentrataDataProvider.useContext = () => {
  return useContext(VentrataDataContext)
}

VentrataDataProvider.useData = () => {
  const context = VentrataDataProvider.useContext()
  return context.data
}

VentrataDataProvider.useProduct = (mode?: GetterMode) => {
  const context = VentrataDataProvider.useContext()
  const productId = InputProvider.useProductId()

  if (mode === 'optional') {
    return context.data.product
  }

  if (!context.data.product.id) {
    throw new Error(`Product is not loaded, product id '${productId}'`)
  }

  return context.data.product
}

VentrataDataProvider.useAmpleOptions = (maxUnits: number = 0, mode?: GetterMode) => {
  const product = VentrataDataProvider.useProduct(mode)

  return product.options.filter((option) => {
    return !option.restrictions?.maxUnits || option.restrictions.maxUnits >= maxUnits
  })
}

VentrataDataProvider.useOption = (optionId: string, mode?: GetterMode) => {
  const productId = InputProvider.useProductId()
  const product = VentrataDataProvider.useProduct(mode)

  const option = product.options.find((option) => option.id === optionId)

  if (mode === 'optional') {
    return option || DEFAULT_VENTRATA_OPTION
  }

  if (!option) {
    throw new Error(`Can't find option by id, product id '${productId}', option id '${optionId}'`)
  }

  return option
}

VentrataDataProvider.useUnits = (mode?: GetterMode) => {
  const productId = InputProvider.useProductId()
  const product = VentrataDataProvider.useProduct(mode)
  const optionId = InputProvider.useOptionId('optional')

  if (product.options.length === 0) {
    if (mode === 'optional') {
      return []
    }

    throw new Error(`Product option list is empty, product id '${productId}'`)
  }

  const selectedOption = product.options.find((o) => o.id === optionId)
  return (selectedOption ?? product.options[0]).units
}

VentrataDataProvider.useUnit = (unitId: string, mode?: GetterMode) => {
  const productId = InputProvider.useProductId()
  const units = VentrataDataProvider.useUnits(mode)

  if (units.length === 0) {
    if (mode === 'optional') {
      return DEFAULT_VENTRATA_UNIT
    }

    throw new Error(`Product unit list is empty, product id '${productId}'`)
  }

  const unit = units.find((unit) => unit.id === unitId)

  if (!unit) {
    if (mode === 'optional') {
      return DEFAULT_VENTRATA_UNIT
    }

    throw new Error(`Can't find unit by id, product id '${productId}', unit id '${unitId}'`)
  }

  return unit
}

VentrataDataProvider.useCalendar = (mode?: GetterMode) => {
  const context = VentrataDataProvider.useContext()

  if (mode === 'optional') {
    return context.data.calendar
  }

  if (Object.keys(context.data.calendar).length === 0) {
    throw new Error(`Calendar is not loaded`)
  }

  return context.data.calendar
}

VentrataDataProvider.useCalendarByOptionId = (optionId: string, mode?: GetterMode) => {
  const calendar = VentrataDataProvider.useCalendar(mode)
  return calendar[optionId]
}

VentrataDataProvider.useAvailabilities = (mode?: GetterMode) => {
  const context = VentrataDataProvider.useContext()

  if (mode === 'optional') {
    return context.data.availabilities
  }

  if (size(context.data.availabilities) === 0) {
    throw new Error(`Availabilities is not loaded`)
  }

  return context.data.availabilities
}

VentrataDataProvider.useIsPending = () => {
  const context = VentrataDataProvider.useContext()
  return context.isPending
}

VentrataDataProvider.useError = () => {
  const context = VentrataDataProvider.useContext()
  return context.error
}
